import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtM, fmtFull } from "@/lib/api";
import { LOGO_SRC, TopNav } from "@/pages/Marketing";
import { BadgeCheck, ShieldCheck, Download, ArrowRight, QrCode, Search, Loader2, XCircle } from "lucide-react";

/* Shared formal certificate renderer — mirrors the PDF template.
   paid=false → simplified web version with FREE TIER watermark diagonal. */
export function CertificateView({ company, midpoint, low, high, methods, dateStr, certId, tier, verifyUrl, paid, compact = false }) {
  const ms = [
    ["DCF", methods?.dcf_valuation, "25%"], ["First Chicago", methods?.first_chicago_valuation, "20%"],
    ["VC Method", methods?.vc_method_valuation, "20%"], ["Scorecard", methods?.scorecard_valuation, "15%"],
    ["Comps", methods?.public_comps_valuation, "20%"],
  ];
  return (
    <div className="relative bg-white rounded-[10px] border-[3px] border-[#16294D] outline outline-1 outline-[#F5C86B] outline-offset-[5px] overflow-hidden" data-testid="certificate-view">
      {["top-1 left-1", "top-1 right-1", "bottom-1 left-1", "bottom-1 right-1"].map((p) => (
        <span key={p} className={`absolute ${p} w-6 h-6 rounded-md bg-[#16294D] text-white text-[13px] font-bold flex items-center justify-center z-10`}>✓</span>
      ))}
      {!paid && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center overflow-hidden">
          <span className="rotate-[-24deg] text-[52px] sm:text-[72px] font-black tracking-[0.2em] text-[#174C82]/15 border-4 border-[#174C82]/20 rounded-xl px-8 py-2 whitespace-nowrap" data-testid="free-tier-watermark">FREE TIER</span>
        </div>
      )}
      <div className="relative px-6 sm:px-10 py-8 text-center">
        <img src={LOGO_SRC} alt="ValueStackHQ" className="h-10 w-auto mx-auto object-contain" />
        <h2 className="font-display font-bold text-[#16294D] tracking-wide mt-4" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: compact ? 26 : 34 }}>CERTIFICATE OF VALUATION</h2>
        <div className="flex items-center gap-3 max-w-[520px] mx-auto mt-2">
          <span className="flex-1 h-px bg-[#16294D]" /><span className="text-[#174C82] font-bold">✓</span><span className="flex-1 h-px bg-[#16294D]" />
        </div>
        <p className="text-slate-500 text-[14px] mt-3">This certifies that</p>
        <div className="font-bold text-[#16294D] mt-1 leading-tight" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: (company || "").length > 30 ? 26 : 36 }} data-testid="certificate-company">{company}</div>
        <p className="text-slate-600 text-[13px] mt-2">has been valued as of {dateStr} using a weighted, multi-method analysis.</p>
        <div className="mt-5 rounded-2xl border border-[#F5C86B] bg-[#FFF8EB] px-6 py-6">
          <div className="text-[11px] font-semibold tracking-[0.2em] text-[#16294D]">ESTIMATED ENTERPRISE VALUE</div>
          <div className="font-mono-num font-bold text-[#174C82] leading-none mt-1" style={{ fontSize: compact ? 40 : 56 }} data-testid="certificate-midpoint">{fmtFull(midpoint)}</div>
          <div className="h-px bg-[#F5C86B] my-4" />
          <div className="text-[11px] font-semibold tracking-[0.2em] text-[#16294D]">VALUATION RANGE</div>
          <div className="font-mono-num font-bold text-[#16294D] text-[20px] mt-1" data-testid="certificate-range">{fmtFull(low)} — {fmtFull(high)}</div>
        </div>
        <div className="mt-4 rounded-xl bg-[#FFF8EB] border border-[#F5C86B] overflow-hidden">
          <div className="text-[10px] font-bold tracking-[0.25em] text-[#16294D] py-2 border-b border-[#F5C86B]">METHODOLOGY</div>
          <div className="grid grid-cols-5 divide-x divide-[#F5E3BC]">
            {ms.map(([n, v, w]) => (
              <div key={n} className="py-3 px-1">
                <div className="text-[11px] sm:text-[12px] font-bold text-[#16294D]">{n}</div>
                <div className="font-mono-num text-[11px] sm:text-[12px] text-slate-500 mt-0.5">{fmtM(v)}</div>
                <div className="text-[10px] text-slate-400">weight {w}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 items-center mt-6 text-left">
          <div className="text-[11px] text-slate-600">
            <div className="font-semibold tracking-wider">VALUATION DATE</div><div>{dateStr}</div>
            <div className="font-semibold tracking-wider mt-3">CERTIFICATE ID</div><div className="font-mono-num" data-testid="certificate-id">{paid ? certId : "WEB-VIEW (no ID)"}</div>
          </div>
          <div className="flex justify-center">
            <div className="w-[104px] h-[104px] rounded-full border-[5px] border-emerald-600 flex flex-col items-center justify-center text-emerald-700 bg-white shadow-sm">
              <span className="text-[8px] font-black tracking-widest">VALUATION</span>
              <span className="text-emerald-600 text-[26px] font-black leading-none">✓✓</span>
              <span className="text-[8px] font-black tracking-widest">VERIFIED</span>
              <span className="text-[7px] font-bold">ValueStackHQ</span>
            </div>
          </div>
          <div className="text-[11px] text-slate-600 text-right">
            <div className="font-semibold tracking-wider">AUTHORIZED BY</div><div>ValueStackHQ Valuation Team</div>
            <div className="h-px bg-[#16294D] my-2" />
            {paid ? (<><div>Verify authenticity</div><div className="font-mono-num text-[9px] text-slate-400 break-all">{verifyUrl}</div></>) : (<div className="text-slate-400">Free web preview — upgrade for a verifiable certificate + QR.</div>)}
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mt-5">Prepared using ValueStackHQ's five-method valuation framework. This certificate is an analytical estimate and not a fairness opinion, audit, or guarantee of future value.</p>
      </div>
    </div>
  );
}

export function VerifyPage() {
  const { certId: routeId } = useParams();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(null); setErr("");
    if (!routeId) return;
    setLoading(true);
    (async () => {
      try { const r = await api.get(`/verify/${encodeURIComponent(routeId)}`); setData(r.data); }
      catch { setErr("No certificate found with this ID. Please check the ID and try again."); }
      finally { setLoading(false); }
    })();
  }, [routeId]);

  const submit = (e) => {
    e.preventDefault();
    const id = query.trim();
    if (id) nav(`/verify/${encodeURIComponent(id)}`);
  };

  // Landing (no ID in URL): show a search box to look up a certificate.
  if (!routeId) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
        <div className="mx-auto max-w-[640px] px-4 py-14">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] text-[#174C82] bg-[#FFF8EB] border border-[#F5C86B] rounded-full px-3 py-1.5"><ShieldCheck size={13} /> CERTIFICATE VERIFICATION</span>
            <h1 className="font-display text-[34px] sm:text-[42px] font-bold tracking-tight mt-4">Verify a valuation certificate<span className="text-[#174C82]">.</span></h1>
            <p className="text-slate-500 mt-2 text-[16px]">Enter the Certificate ID printed on any ValueStackHQ certificate (e.g. <span className="font-mono-num text-slate-700">VSHQ-9BD4F6</span>) to confirm it is authentic.</p>
          </div>
          <form onSubmit={submit} className="card-vs p-5 mt-8 flex flex-col sm:flex-row gap-3" data-testid="verify-search-form">
            <div className="relative flex-1">
              <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Certificate ID (e.g. VSHQ-9BD4F6)" className="w-full h-12 rounded-lg border border-slate-300 pl-10 pr-3 font-mono-num focus:border-[#174C82] focus:ring-2 focus:ring-[#F5C86B] focus:outline-none" data-testid="verify-id-input" />
            </div>
            <button type="submit" className="btn-primary-vs h-12 px-6 font-semibold flex items-center justify-center gap-2 whitespace-nowrap" data-testid="verify-lookup-button"><ShieldCheck size={17} /> Verify certificate</button>
          </form>
          <div className="flex items-center gap-3 mt-8 text-slate-500 text-[13px]">
            <span className="flex-1 h-px bg-slate-200" /> or <span className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="text-center mt-4">
            <Link to="/pricing" className="link-arrow text-[#174C82] font-semibold text-[14px] inline-flex items-center gap-1">Get your own verifiable certificate <ArrowRight size={15} /></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[760px] px-4 py-10">
        {loading && <div className="card-vs p-8 text-center text-slate-500 flex items-center justify-center gap-2" data-testid="verify-loading"><Loader2 className="animate-spin" size={18} /> Verifying certificate {routeId}…</div>}
        {err && !loading && (
          <div className="card-vs p-8 text-center" data-testid="verify-not-found">
            <XCircle size={40} className="mx-auto text-red-500" />
            <div className="font-bold text-[18px] mt-2">Not verified</div>
            <p className="text-slate-500 text-[14px] mt-1">{err}</p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Link to="/verify" className="text-[#174C82] font-semibold text-[14px]">Try another ID</Link>
              <Link to="/" className="text-slate-500 font-semibold text-[14px]">← Back home</Link>
            </div>
          </div>
        )}
        {data && !loading && (<>
          <div className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-2 font-semibold ${data.valid ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-700"}`} data-testid="verify-status">
            {data.valid ? <><BadgeCheck size={18} /> Authentic certificate · {data.cert_id}</> : <><XCircle size={18} /> Invalid or revoked certificate</>}
          </div>
          <CertificateView company={data.company} midpoint={data.midpoint} low={data.range_low} high={data.range_high} methods={data.methods} dateStr={(data.issued_at || "").slice(0, 10)} certId={data.cert_id} tier={data.tier} verifyUrl={`${window.location.origin}/verify/${data.cert_id}`} paid />
          <div className="text-center mt-4 flex items-center justify-center gap-5">
            <Link to="/verify" className="text-slate-500 font-semibold text-[14px] inline-flex items-center gap-1"><Search size={14} /> Verify another</Link>
            <Link to="/" className="link-arrow text-[#174C82] font-semibold text-[14px] inline-flex items-center gap-1">Get your own valuation <ArrowRight size={15} /></Link>
          </div>
        </>)}
      </div>
    </div>
  );
}
